import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Typography,
  Box,
  Divider,
  FormControlLabel,
  Checkbox,
  IconButton,
  Paper,
  Alert
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import axios from '../utils/axios';
import { STUDENT_CATEGORY_LABELS, priceForCategory } from '../utils/campusMinistryFields';

const emptyContact = () => ({ name: '', address: '', phoneDay: '', phoneEvening: '' });
const emptyMedication = () => ({ name: '', prescribingPhysician: '', phone: '' });

const emptyForm = () => ({
  eventId: '',
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  email: '',
  studentCategory: '',
  roommateChoices: ['', '', ''],
  emergencyContacts: [emptyContact(), emptyContact()],
  primaryDoctor: { name: '', phone: '' },
  medications: [emptyMedication()],
  medicalConditions: '',
  allergies: { medicines: '', other: '' },
  insurance: { carrier: '', plan: '', policyNumber: '' },
  payment: { amount: '', method: 'cash' },
  signature: { name: '', date: '' },
  paperConsentOnFile: false
});

const toDateInput = (d) => {
  if (!d) return '';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const padTo = (list, n, make) => {
  const out = [...(list || [])];
  while (out.length < n) out.push(make());
  return out;
};

// Build the editable form state from an existing registration (deep, with safe defaults).
const fromRegistration = (reg) => {
  const base = emptyForm();
  if (!reg) return base;
  return {
    ...base,
    eventId: reg.eventId || '',
    firstName: reg.firstName || '',
    lastName: reg.lastName || '',
    dateOfBirth: toDateInput(reg.dateOfBirth),
    email: reg.email || '',
    studentCategory: reg.studentCategory || '',
    roommateChoices: padTo(reg.roommateChoices, 3, () => ''),
    emergencyContacts: padTo((reg.emergencyContacts || []).map(c => ({ ...emptyContact(), ...c })), 2, emptyContact),
    primaryDoctor: { ...base.primaryDoctor, ...(reg.primaryDoctor || {}) },
    medications: padTo((reg.medications || []).map(m => ({ ...emptyMedication(), ...m })), 1, emptyMedication),
    medicalConditions: reg.medicalConditions || '',
    allergies: { ...base.allergies, ...(reg.allergies || {}) },
    insurance: { ...base.insurance, ...(reg.insurance || {}) },
    payment: { amount: reg.payment?.amount ?? 0, method: reg.payment?.method || 'cash' },
    signature: { name: reg.signature?.name || '', date: toDateInput(reg.signature?.date) }
  };
};

const SectionTitle = ({ children }) => (
  <Typography variant="h6" sx={{ mb: 1.5, color: 'primary.main' }}>{children}</Typography>
);

// Manual add (staff entering a paper form) and edit of a Campus Ministry registration.
const CampusMinistryForm = ({ open, onClose, onSuccess, registration, events = [], defaultEventId }) => {
  const isEdit = Boolean(registration);
  const [form, setForm] = useState(emptyForm());
  const [sendEmails, setSendEmails] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    const next = fromRegistration(registration);
    if (!isEdit) next.eventId = defaultEventId || (events.find(e => e.isCurrent) || events[0] || {})._id || '';
    setForm(next);
    setSendEmails(false);
    setError(null);
  }, [open, registration]); // eslint-disable-line react-hooks/exhaustive-deps

  const setField = (path, value) => {
    setForm(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let cur = next;
      keys.forEach((k, i) => {
        if (i === keys.length - 1) cur[k] = value;
        else { if (!cur[k] || typeof cur[k] !== 'object') cur[k] = {}; cur = cur[k]; }
      });
      return next;
    });
  };

  // New registration: default the amount from the event's price tier.
  const handleCategoryChange = (value) => {
    setField('studentCategory', value);
    if (!isEdit) {
      const event = events.find(e => e._id === form.eventId);
      setField('payment.amount', priceForCategory(event, value));
    }
  };

  const handleSubmit = async () => {
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim() || !form.dateOfBirth) {
      setError('First name, last name, date of birth and email are required.');
      return;
    }
    if (!isEdit && !form.eventId) {
      setError('Please choose an event.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const { paperConsentOnFile, eventId, ...rest } = form;
      const payload = {
        ...rest,
        roommateChoices: form.roommateChoices.map(s => s.trim()).filter(Boolean),
        emergencyContacts: form.emergencyContacts.filter(c => c.name || c.phoneDay || c.address || c.phoneEvening),
        medications: form.medications.filter(m => m.name || m.prescribingPhysician || m.phone),
        payment: { amount: Number(form.payment.amount) || 0, method: form.payment.method },
        signature: { name: form.signature.name, date: form.signature.date || undefined }
      };
      if (!payload.studentCategory) delete payload.studentCategory;

      let response;
      if (isEdit) {
        response = await axios.put(`/campus-ministry/${registration._id}`, payload);
      } else {
        payload.eventId = eventId;
        payload.consent = paperConsentOnFile ? { agreed: true, agreedAt: new Date().toISOString() } : { agreed: false };
        payload.notifications = { sendEmails };
        response = await axios.post('/campus-ministry', payload);
      }
      if (onSuccess) onSuccess(response.data);
      onClose();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to save registration.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>{isEdit ? `Edit Registration — ${registration.eventName || ''}` : 'New Campus Ministry Registration'}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!isEdit && (
          <TextField select fullWidth label="Event" value={form.eventId} onChange={(e) => setField('eventId', e.target.value)} sx={{ mb: 2 }}>
            {events.map(ev => <MenuItem key={ev._id} value={ev._id}>{ev.name}</MenuItem>)}
          </TextField>
        )}

        <SectionTitle>Participant</SectionTitle>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}><TextField fullWidth required label="First Name" value={form.firstName} onChange={(e) => setField('firstName', e.target.value)} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth required label="Last Name" value={form.lastName} onChange={(e) => setField('lastName', e.target.value)} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth required type="date" label="Date of Birth" InputLabelProps={{ shrink: true }} value={form.dateOfBirth} onChange={(e) => setField('dateOfBirth', e.target.value)} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth required type="email" label="Email" value={form.email} onChange={(e) => setField('email', e.target.value)} /></Grid>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth label="Student Category" value={form.studentCategory} onChange={(e) => handleCategoryChange(e.target.value)}>
              {Object.entries(STUDENT_CATEGORY_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
            </TextField>
          </Grid>
        </Grid>
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Roommate Choice(s)</Typography>
        <Grid container spacing={2}>
          {form.roommateChoices.map((name, i) => (
            <Grid item xs={12} sm={4} key={i}>
              <TextField fullWidth label={`Roommate ${i + 1}`} value={name} onChange={(e) => setField(`roommateChoices.${i}`, e.target.value)} />
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 2 }} />
        <SectionTitle>Emergency Contacts</SectionTitle>
        {form.emergencyContacts.map((c, i) => (
          <Box key={i} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Emergency Contact {i + 1}</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Name" value={c.name} onChange={(e) => setField(`emergencyContacts.${i}.name`, e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Address" value={c.address} onChange={(e) => setField(`emergencyContacts.${i}.address`, e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Phone (day)" value={c.phoneDay} onChange={(e) => setField(`emergencyContacts.${i}.phoneDay`, e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Phone (evening)" value={c.phoneEvening} onChange={(e) => setField(`emergencyContacts.${i}.phoneEvening`, e.target.value)} /></Grid>
            </Grid>
          </Box>
        ))}

        <Divider sx={{ my: 2 }} />
        <SectionTitle>Medical & Insurance</SectionTitle>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Primary Doctor" value={form.primaryDoctor.name} onChange={(e) => setField('primaryDoctor.name', e.target.value)} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Doctor Phone" value={form.primaryDoctor.phone} onChange={(e) => setField('primaryDoctor.phone', e.target.value)} /></Grid>
        </Grid>
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Medications</Typography>
        {form.medications.map((m, i) => (
          <Paper key={i} variant="outlined" sx={{ p: 2, mb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2">Medication {i + 1}</Typography>
              {form.medications.length > 1 && (
                <IconButton size="small" color="error" onClick={() => setForm(prev => ({ ...prev, medications: prev.medications.filter((_, idx) => idx !== i) }))}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}><TextField fullWidth label="Name of Medication" value={m.name} onChange={(e) => setField(`medications.${i}.name`, e.target.value)} /></Grid>
              <Grid item xs={12} sm={4}><TextField fullWidth label="Prescribing Physician" value={m.prescribingPhysician} onChange={(e) => setField(`medications.${i}.prescribingPhysician`, e.target.value)} /></Grid>
              <Grid item xs={12} sm={4}><TextField fullWidth label="Phone" value={m.phone} onChange={(e) => setField(`medications.${i}.phone`, e.target.value)} /></Grid>
            </Grid>
          </Paper>
        ))}
        <Button size="small" startIcon={<AddIcon />} onClick={() => setForm(prev => ({ ...prev, medications: [...prev.medications, emptyMedication()] }))}>
          Add Medication
        </Button>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}><TextField fullWidth multiline minRows={2} label="Medical Conditions" value={form.medicalConditions} onChange={(e) => setField('medicalConditions', e.target.value)} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth multiline label="Allergies — To medicines" value={form.allergies.medicines} onChange={(e) => setField('allergies.medicines', e.target.value)} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth multiline label="Allergies — Other" value={form.allergies.other} onChange={(e) => setField('allergies.other', e.target.value)} /></Grid>
          <Grid item xs={12} sm={4}><TextField fullWidth label="Insurance Carrier" value={form.insurance.carrier} onChange={(e) => setField('insurance.carrier', e.target.value)} /></Grid>
          <Grid item xs={12} sm={4}><TextField fullWidth label="Plan" value={form.insurance.plan} onChange={(e) => setField('insurance.plan', e.target.value)} /></Grid>
          <Grid item xs={12} sm={4}><TextField fullWidth label="Policy #" value={form.insurance.policyNumber} onChange={(e) => setField('insurance.policyNumber', e.target.value)} /></Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />
        <SectionTitle>Payment & Signature</SectionTitle>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}><TextField fullWidth type="number" label="Amount Due ($)" value={form.payment.amount} onChange={(e) => setField('payment.amount', e.target.value)} InputProps={{ inputProps: { min: 0 } }} /></Grid>
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth label="Payment Method" value={form.payment.method} onChange={(e) => setField('payment.method', e.target.value)}>
              <MenuItem value="cash">In person</MenuItem>
              <MenuItem value="online">Online</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Signature Name" value={form.signature.name} onChange={(e) => setField('signature.name', e.target.value)} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth type="date" label="Signature Date" InputLabelProps={{ shrink: true }} value={form.signature.date} onChange={(e) => setField('signature.date', e.target.value)} /></Grid>
        </Grid>
        {isEdit && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Payments already recorded are kept. Use "Record a Payment" on the registration to add one.
          </Typography>
        )}

        {!isEdit && (
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={<Checkbox checked={form.paperConsentOnFile} onChange={(e) => setField('paperConsentOnFile', e.target.checked)} />}
              label="Signed paper consent form is on file"
            />
            <FormControlLabel
              control={<Checkbox checked={sendEmails} onChange={(e) => setSendEmails(e.target.checked)} />}
              label="Send confirmation email to participant and notification to staff"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'Saving...' : (isEdit ? 'Save Changes' : 'Create Registration')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CampusMinistryForm;
